import { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Film, Plus, Trash2, Music2, Save, Play, AlertCircle,
  ArrowRight, Clock, Scissors, GripVertical, Layers, X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import PublishToCommunityButton from "../components/PublishToCommunityButton";
import { ExportToPlatformButton } from "../components/WaveTwoFeatures";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASPECT_OPTIONS = [
  { id: "9:16", label: "9:16 · Reels" },
  { id: "1:1",  label: "1:1 · Square" },
  { id: "16:9", label: "16:9 · Wide" },
  { id: "4:5",  label: "4:5 · Portrait" },
];

const TRANSITIONS = [
  { id: "cut",       label: "Hard cut",   blurb: "Instant clip change. Snappy." },
  { id: "crossfade", label: "Crossfade",  blurb: "0.5 s blend between clips." },
];

const AUDIO_MODES = [
  { id: "mix",          label: "Mix",         blurb: "Clip audio + music" },
  { id: "music_only",   label: "Music only",  blurb: "Silence the clips" },
  { id: "clips_audio",  label: "Clips audio", blurb: "Skip the music" },
];

const TimelineEditorPage = () => {
  const { token } = useAuth();
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [sources, setSources] = useState({ images: [], videos: [], cgi: [] });
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rendering, setRendering] = useState(false);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([
          axios.get(`${API}/timeline/sources`, { headers: auth }),
          axios.get(`${API}/timeline/projects`, { headers: auth }),
        ]);
        setSources(s.data || { images: [], videos: [], cgi: [] });
        setProjects(p.data.items || []);
      } catch (e) {
        toast.error("Could not load Timeline Editor.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Poll for status updates of in-progress renders every 6s (live progress)
  useEffect(() => {
    const id = setInterval(async () => {
      const inFlight = projects.some(p =>
        p.status === "queued" || p.status === "rendering"
      );
      if (!inFlight) return;
      try {
        const r = await axios.get(`${API}/timeline/projects`, { headers: auth });
        setProjects(r.data.items || []);
      } catch (_e) { /* poll silently */ }
    }, 6000);
    return () => clearInterval(id);
  }, [projects, auth]);

  const totalDuration = useMemo(() => {
    if (!activeProject) return 0;
    const raw = (activeProject.clips || []).reduce((a, c) => a + Number(c.duration || 0), 0);
    if (activeProject.transition === "crossfade") {
      return Math.max(0, raw - Math.max(0, (activeProject.clips.length - 1)) * 0.5);
    }
    return raw;
  }, [activeProject]);

  const newProject = () => {
    setActiveProject({
      id: null,
      title: "Untitled Timeline",
      clips: [],
      music: null,
      audio_mode: "mix",
      transition: "cut",
      aspect_ratio: "9:16",
      status: "draft",
    });
  };

  const openProject = async (projectId) => {
    try {
      const r = await axios.get(`${API}/timeline/projects/${projectId}`, { headers: auth });
      setActiveProject(r.data.project);
    } catch (e) {
      toast.error("Could not open project.");
    }
  };

  const allSources = useMemo(
    () => [...sources.videos, ...sources.cgi, ...sources.images],
    [sources]
  );

  const addClip = (src) => {
    if (!activeProject) return;
    if ((activeProject.clips?.length || 0) >= 24) {
      toast.error("Max 24 clips per timeline.");
      return;
    }
    const defaultDuration = src.kind === "video" ? Math.min(src.duration || 5, 10) : 3;
    const clip = {
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      source_collection: src.collection,
      source_id: src.id,
      kind: src.kind,
      media_url: src.media_url,
      thumbnail_url: src.thumbnail_url,
      start_time: 0,
      end_time: src.kind === "video" ? defaultDuration : 0,
      duration: defaultDuration,
      _source: src,
    };
    setActiveProject(p => ({ ...p, clips: [...(p.clips || []), clip] }));
  };

  const setMusic = (src) => {
    if (!activeProject) return;
    setActiveProject(p => ({
      ...p,
      music: {
        source_collection: src.collection,
        source_id: src.id,
        media_url: src.media_url,
        volume: 0.6,
        start_offset: 0,
      },
    }));
  };

  const updateClip = (clipId, patch) => {
    setActiveProject(p => ({
      ...p,
      clips: p.clips.map(c => c.id === clipId ? { ...c, ...patch } : c),
    }));
  };

  const removeClip = (clipId) => {
    setActiveProject(p => ({
      ...p,
      clips: p.clips.filter(c => c.id !== clipId),
    }));
  };

  const moveClip = (clipId, direction) => {
    setActiveProject(p => {
      const idx = p.clips.findIndex(c => c.id === clipId);
      if (idx < 0) return p;
      const j = direction === "left" ? idx - 1 : idx + 1;
      if (j < 0 || j >= p.clips.length) return p;
      const next = [...p.clips];
      [next[idx], next[j]] = [next[j], next[idx]];
      return { ...p, clips: next };
    });
  };

  // Drag-and-drop reorder: move the clip at fromIdx to be at toIdx.
  const reorderClips = (fromIdx, toIdx) => {
    setActiveProject(p => {
      if (
        fromIdx === toIdx ||
        fromIdx < 0 || toIdx < 0 ||
        fromIdx >= p.clips.length || toIdx >= p.clips.length
      ) return p;
      const next = [...p.clips];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return { ...p, clips: next };
    });
  };

  const buildPayload = () => ({
    title: activeProject.title,
    audio_mode: activeProject.audio_mode,
    transition: activeProject.transition,
    aspect_ratio: activeProject.aspect_ratio,
    clips: (activeProject.clips || []).map(c => ({
      source_collection: c.source_collection,
      source_id: c.source_id,
      start_time: Number(c.start_time || 0),
      end_time:   Number(c.end_time   || 0),
      duration:   Number(c.duration   || 3),
    })),
    music: activeProject.music ? {
      source_collection: activeProject.music.source_collection,
      source_id: activeProject.music.source_id,
      volume: Number(activeProject.music.volume || 0.6),
      start_offset: Number(activeProject.music.start_offset || 0),
    } : null,
  });

  const saveProject = async () => {
    if (!activeProject) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      let r;
      if (activeProject.id) {
        r = await axios.put(`${API}/timeline/projects/${activeProject.id}`, payload, { headers: auth });
      } else {
        r = await axios.post(`${API}/timeline/projects`, payload, { headers: auth });
      }
      setActiveProject(r.data.project);
      setProjects(prev => {
        const others = prev.filter(p => p.id !== r.data.project.id);
        return [r.data.project, ...others];
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const queueRender = async () => {
    if (!activeProject) return;
    if ((activeProject.clips?.length || 0) === 0) {
      toast.error("Drag in at least one clip first.");
      return;
    }
    // Save first to capture latest trims
    setRendering(true);
    try {
      const payload = buildPayload();
      let proj = activeProject;
      if (activeProject.id) {
        const r = await axios.put(`${API}/timeline/projects/${activeProject.id}`, payload, { headers: auth });
        proj = r.data.project;
      } else {
        const r = await axios.post(`${API}/timeline/projects`, payload, { headers: auth });
        proj = r.data.project;
      }

      const confirm = window.confirm(
        "Rendering a multi-clip timeline can take several minutes. " +
        "You can safely close this browser tab — we'll email you the moment your render is ready.\n\nContinue?"
      );
      if (!confirm) {
        setActiveProject(proj);
        setRendering(false);
        return;
      }

      const r2 = await axios.post(`${API}/timeline/projects/${proj.id}/render`, {}, { headers: auth });
      setActiveProject(r2.data.project);
      setProjects(prev => {
        const others = prev.filter(p => p.id !== r2.data.project.id);
        return [r2.data.project, ...others];
      });
      toast.success("Render queued. We'll email you when it's ready 🎬");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not queue render.");
    } finally {
      setRendering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div
      className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-8 space-y-6 sm:space-y-8"
      data-testid="timeline-editor-page"
    >
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-blue-500 flex items-center justify-center shadow-md">
            <Layers className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-500">
              Timeline Editor · Beta
            </p>
            <h1 className="text-3xl md:text-4xl font-black text-ink-950 tracking-tight">
              Sequence clips, sync music, render an mp4.
            </h1>
          </div>
        </div>

        <Button
          onClick={newProject}
          data-testid="timeline-new-project"
          className="bg-blue-500 hover:bg-blue-600 text-white"
        >
          <Plus className="w-4 h-4 mr-1" /> New project
        </Button>
      </div>

      {/* Copyright clean banner */}
      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900">
          Only your XEA-generated clips (Marketing, Designer, CGI) can be dropped on the timeline.
          Uploads are intentionally disabled to keep your renders 100% copyright-clean.
        </p>
      </div>

      {/* Project list */}
      {!activeProject && (
        <section data-testid="timeline-project-list">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-3">
            Your projects
          </p>
          {projects.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-ink-200 p-8 text-center bg-white text-sm text-ink-500">
              No projects yet — click <strong>New project</strong> to start your first sequence.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {projects.map(p => {
                const inFlight = p.status === "queued" || p.status === "rendering";
                const pct = Math.max(0, Math.min(100, Number(p.progress || 0)));
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => openProject(p.id)}
                    data-testid={`timeline-project-${p.id}`}
                    className="text-left bg-white border border-ink-200 rounded-2xl p-4 hover:border-ink-950 transition-all"
                  >
                    <p className="text-base font-bold text-ink-950 truncate">{p.title}</p>
                    <p className="text-xs text-ink-500 mt-1">
                      {(p.clips || []).length} clips · {Number(p.total_duration || 0).toFixed(1)}s · {p.aspect_ratio}
                    </p>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <span
                        className={`inline-block text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-full ${
                          p.status === "completed" ? "bg-emerald-100 text-emerald-700"
                          : p.status === "failed"  ? "bg-rose-100 text-rose-700"
                          : inFlight                ? "bg-amber-100 text-amber-700"
                                                   : "bg-ink-100 text-ink-600"
                        }`}
                      >
                        {p.status}
                      </span>
                      {inFlight && (
                        <span
                          data-testid={`timeline-project-${p.id}-pct`}
                          className="text-[11px] font-bold text-blue-500 tabular-nums"
                        >
                          {pct}%
                        </span>
                      )}
                      {p.result_url && (
                        <a
                          href={p.result_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="text-[10px] font-bold text-blue-500 underline"
                        >
                          view mp4
                        </a>
                      )}
                    </div>
                    {inFlight && (
                      <div
                        className="mt-2 flex flex-col gap-1"
                        data-testid={`timeline-project-${p.id}-progress`}
                      >
                        <div className="h-1.5 bg-ink-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 transition-all duration-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          {p.progress_label && (
                            <span className="text-[10px] text-ink-500 truncate">
                              {p.progress_label}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm("Cancel this render? It can't be resumed.")) return;
                              try {
                                await axios.post(`${API}/timeline/projects/${p.id}/cancel`, {}, { headers: auth });
                                setProjects(prev => prev.map(x => x.id === p.id
                                  ? { ...x, status: "cancelled", progress_label: "Cancelled by user" }
                                  : x));
                                toast.success("Render cancelled");
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Could not cancel.");
                              }
                            }}
                            data-testid={`timeline-project-${p.id}-cancel`}
                            className="text-[10px] font-bold text-rose-500 hover:text-rose-700 inline-flex items-center gap-0.5 ml-auto"
                          >
                            <X className="w-3 h-3" /> cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Editor */}
      {activeProject && (
        <ProjectEditor
          project={activeProject}
          setProject={setActiveProject}
          allSources={allSources}
          videoSources={sources.videos || []}
          totalDuration={totalDuration}
          onAddClip={addClip}
          onUpdateClip={updateClip}
          onRemoveClip={removeClip}
          onMoveClip={moveClip}
          onReorderClips={reorderClips}
          onSetMusic={setMusic}
          onSave={saveProject}
          onQueueRender={queueRender}
          onBack={() => setActiveProject(null)}
          saving={saving}
          rendering={rendering}
        />
      )}
    </div>
  );
};


/* ─────────────────────────────────────────────────────────────────────────
   Project Editor
   ──────────────────────────────────────────────────────────────────────── */

const ProjectEditor = ({
  project, setProject, allSources, videoSources, totalDuration,
  onAddClip, onUpdateClip, onRemoveClip, onMoveClip, onReorderClips, onSetMusic,
  onSave, onQueueRender, onBack, saving, rendering,
}) => {
  const updateField = (field, value) =>
    setProject(p => ({ ...p, [field]: value }));

  return (
    <div className="space-y-6" data-testid="timeline-editor">
      {/* Title + meta row */}
      <div className="bg-white border border-ink-200 rounded-3xl p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            data-testid="timeline-back"
            className="text-xs font-bold text-ink-500 hover:text-ink-950"
          >
            ← Projects
          </button>
          <input
            type="text"
            value={project.title}
            onChange={(e) => updateField("title", e.target.value)}
            maxLength={120}
            data-testid="timeline-title"
            className="flex-1 min-w-[200px] text-xl font-bold text-ink-950 bg-transparent outline-none border-b border-transparent focus:border-blue-500"
          />
          <span className="text-xs text-ink-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> {totalDuration.toFixed(1)}s · {project.aspect_ratio}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Aspect */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2">
              Canvas
            </p>
            <div className="flex flex-wrap gap-1.5">
              {ASPECT_OPTIONS.map(a => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => updateField("aspect_ratio", a.id)}
                  data-testid={`timeline-aspect-${a.id}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${
                    project.aspect_ratio === a.id
                      ? "bg-blue-500 text-white border-blue-500"
                      : "bg-white border-ink-200"
                  }`}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          {/* Transition */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2">
              Transition
            </p>
            <div className="flex flex-wrap gap-1.5">
              {TRANSITIONS.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => updateField("transition", t.id)}
                  data-testid={`timeline-transition-${t.id}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${
                    project.transition === t.id
                      ? "bg-blue-500 text-white border-blue-500"
                      : "bg-white border-ink-200"
                  }`}
                  title={t.blurb}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Audio mode */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2">
              Audio
            </p>
            <div className="flex flex-wrap gap-1.5">
              {AUDIO_MODES.map(a => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => updateField("audio_mode", a.id)}
                  data-testid={`timeline-audio-${a.id}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${
                    project.audio_mode === a.id
                      ? "bg-blue-500 text-white border-blue-500"
                      : "bg-white border-ink-200"
                  }`}
                  title={a.blurb}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Source library */}
      <section>
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-3">
          Step 1 · Drop clips into the timeline
        </p>
        {allSources.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-ink-200 p-8 text-center bg-white text-sm text-ink-500">
            No generations yet — head to Designer / Marketing / CGI mode to create something, then come back.
          </div>
        ) : (
          <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-8 gap-2">
            {allSources.slice(0, 32).map(s => (
              <button
                key={`${s.collection}:${s.id}`}
                type="button"
                onClick={() => onAddClip(s)}
                data-testid={`timeline-source-${s.id}`}
                className="relative rounded-2xl overflow-hidden border-2 aspect-square bg-ink-100 border-ink-200 hover:border-ink-950 transition-all group"
              >
                {s.kind === "video" ? (
                  <video src={s.media_url} muted playsInline preload="metadata"
                         className="w-full h-full object-cover" />
                ) : (
                  <img src={s.media_url} alt="" loading="lazy"
                       className="w-full h-full object-cover" />
                )}
                <span className="absolute top-1 left-1 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-black/70 text-white">
                  {s.kind === "video" ? "vid" : "img"}
                </span>
                <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                  <Plus className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Timeline strip */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500">
            Step 2 · Timeline ({project.clips.length} clip{project.clips.length === 1 ? "" : "s"})
          </p>
          {project.clips.length > 0 && (
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-400">
              {totalDuration.toFixed(1)}s total
            </p>
          )}
        </div>

        {project.clips.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-ink-200 p-8 text-center bg-white text-sm text-ink-500">
            Click a source above to add it to your timeline. Drag clips to reorder.
          </div>
        ) : (
          <div className="space-y-2" data-testid="timeline-strip">
            {project.clips.map((c, idx) => (
              <ClipRow
                key={c.id}
                clip={c}
                index={idx}
                total={project.clips.length}
                onUpdate={(patch) => onUpdateClip(c.id, patch)}
                onRemove={() => onRemoveClip(c.id)}
                onMove={(dir) => onMoveClip(c.id, dir)}
                onReorder={onReorderClips}
              />
            ))}
          </div>
        )}
      </section>

      {/* Music track */}
      <section>
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-3">
          Step 3 · Music (optional)
        </p>
        <MusicPicker
          music={project.music}
          allSources={(videoSources || []).filter(s => s.kind === "video")}
          onPick={onSetMusic}
          onUpdate={(patch) => setProject(p => ({ ...p, music: p.music ? { ...p.music, ...patch } : null }))}
          onClear={() => setProject(p => ({ ...p, music: null }))}
        />
      </section>

      {/* Action bar */}
      <div className="sticky bottom-2 z-10">
        <div className="bg-ink-950 text-white rounded-3xl px-5 py-4 flex flex-wrap items-center justify-between gap-3 shadow-xl">
          <p className="text-sm">
            <strong className="text-white">{project.clips.length}</strong> clip{project.clips.length === 1 ? "" : "s"}
            <span className="opacity-50 mx-2">·</span>
            <strong className="text-white">{totalDuration.toFixed(1)}s</strong>
            <span className="opacity-50 mx-2">·</span>
            <span className="opacity-70">{project.aspect_ratio} · {project.transition}</span>
            {project.status && project.status !== "draft" && (
              <span className="ml-2 inline-block text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-white/10">
                {project.status}
              </span>
            )}
          </p>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={onSave}
              disabled={saving}
              data-testid="timeline-save"
              className="text-white hover:bg-white/10"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
            <Button
              onClick={onQueueRender}
              disabled={rendering || project.clips.length === 0}
              data-testid="timeline-render"
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              {rendering ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
              Render mp4
            </Button>
          </div>
        </div>
      </div>

      {project.result_url && project.status === "completed" && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-3xl p-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Film className="w-5 h-5 text-emerald-600" />
            <p className="text-sm font-bold text-emerald-900">Your render is ready.</p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <a
              href={project.result_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="timeline-result-link"
              className="text-sm font-bold text-emerald-700 underline flex items-center gap-1"
            >
              Open mp4 <ArrowRight className="w-3 h-3" />
            </a>
            {project.id && (
              <PublishToCommunityButton
                sourceCollection="timeline_projects"
                sourceId={project.id}
                defaultCaption={project.title}
                testIdPrefix="timeline-publish"
              />
            )}
            {project.id && (
              <ExportToPlatformButton
                sourceCollection="timeline_projects"
                sourceId={project.id}
                testIdPrefix="timeline-export"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};


/* ─────────────────────────────────────────────────────────────────────────
   Clip row
   ──────────────────────────────────────────────────────────────────────── */

const ClipRow = ({ clip, index, total, onUpdate, onRemove, onMove, onReorder }) => {
  const videoRef = useRef(null);
  const isVideo = clip.kind === "video";
  const maxSource = isVideo ? Number(clip._source?.duration || 30) : 0;
  const [dragOver, setDragOver] = useState(false);

  // Native HTML5 drag-drop reorder. Only the grip handle is draggable so
  // text/number inputs inside the row remain selectable.
  const handleDragStart = (e) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/x-xea-clip-idx", String(index));
  };
  const handleDragOver = (e) => {
    if (e.dataTransfer.types.includes("text/x-xea-clip-idx")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (!dragOver) setDragOver(true);
    }
  };
  const handleDragLeave = () => setDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const raw = e.dataTransfer.getData("text/x-xea-clip-idx");
    const from = Number(raw);
    if (Number.isNaN(from) || from === index) return;
    onReorder?.(from, index);
  };

  return (
    <div
      className={`bg-white border rounded-2xl p-3 flex flex-wrap items-center gap-3 transition-all ${
        dragOver ? "border-blue-500 ring-2 ring-blue-200" : "border-ink-200"
      }`}
      data-testid={`timeline-clip-${index}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <span
        draggable
        onDragStart={handleDragStart}
        data-testid={`clip-${index}-drag`}
        className="cursor-grab active:cursor-grabbing shrink-0 px-1 -mx-1 hover:bg-ink-100 rounded"
        title="Drag to reorder"
      >
        <GripVertical className="w-4 h-4 text-ink-300" />
      </span>
      <span className="text-[10px] font-black text-ink-500 w-5 text-center">
        {index + 1}
      </span>

      {/* Thumbnail */}
      <div className="w-16 h-16 rounded-xl overflow-hidden bg-ink-100 shrink-0">
        {isVideo ? (
          <video
            ref={videoRef}
            src={clip.media_url}
            muted playsInline preload="metadata"
            className="w-full h-full object-cover"
          />
        ) : (
          <img src={clip.media_url} alt="" loading="lazy"
               className="w-full h-full object-cover" />
        )}
      </div>

      {/* Trims */}
      <div className="flex-1 min-w-[200px] grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
        {isVideo ? (
          <>
            <label className="flex flex-col">
              <span className="text-[9px] uppercase tracking-wider text-ink-400 font-bold flex items-center gap-1">
                <Scissors className="w-3 h-3" /> In
              </span>
              <input
                type="number"
                step="0.1"
                min="0"
                max={maxSource}
                value={clip.start_time}
                onChange={(e) => {
                  const v = Math.max(0, Math.min(Number(e.target.value), maxSource));
                  const end = Math.max(v + 0.5, clip.end_time);
                  onUpdate({ start_time: v, end_time: end, duration: end - v });
                }}
                data-testid={`clip-${index}-in`}
                className="bg-ink-50 rounded px-2 py-1 outline-none focus:bg-white border border-ink-200"
              />
            </label>
            <label className="flex flex-col">
              <span className="text-[9px] uppercase tracking-wider text-ink-400 font-bold flex items-center gap-1">
                <Scissors className="w-3 h-3" /> Out
              </span>
              <input
                type="number"
                step="0.1"
                min="0.5"
                max={maxSource}
                value={clip.end_time}
                onChange={(e) => {
                  const v = Math.max(clip.start_time + 0.5, Math.min(Number(e.target.value), maxSource));
                  onUpdate({ end_time: v, duration: v - clip.start_time });
                }}
                data-testid={`clip-${index}-out`}
                className="bg-ink-50 rounded px-2 py-1 outline-none focus:bg-white border border-ink-200"
              />
            </label>
            <p className="text-[10px] text-ink-500 self-end pb-1">
              <Clock className="w-3 h-3 inline mr-1" />
              {Number(clip.duration || 0).toFixed(2)}s
            </p>
          </>
        ) : (
          <label className="flex flex-col col-span-3">
            <span className="text-[9px] uppercase tracking-wider text-ink-400 font-bold">
              Hold time (s)
            </span>
            <input
              type="number"
              step="0.5"
              min="0.5"
              max="15"
              value={clip.duration}
              onChange={(e) => {
                const v = Math.max(0.5, Math.min(15, Number(e.target.value)));
                onUpdate({ duration: v });
              }}
              data-testid={`clip-${index}-duration`}
              className="bg-ink-50 rounded px-2 py-1 outline-none focus:bg-white border border-ink-200"
            />
          </label>
        )}
      </div>

      {/* Reorder + delete */}
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={() => onMove("left")}
          disabled={index === 0}
          data-testid={`clip-${index}-up`}
          className="text-ink-400 hover:text-ink-950 disabled:opacity-30 text-xs px-2"
        >
          ↑
        </button>
        <button
          type="button"
          onClick={() => onMove("right")}
          disabled={index === total - 1}
          data-testid={`clip-${index}-down`}
          className="text-ink-400 hover:text-ink-950 disabled:opacity-30 text-xs px-2"
        >
          ↓
        </button>
        <button
          type="button"
          onClick={onRemove}
          data-testid={`clip-${index}-remove`}
          className="text-rose-500 hover:text-rose-700 p-1"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};


/* ─────────────────────────────────────────────────────────────────────────
   Music picker
   ──────────────────────────────────────────────────────────────────────── */

const MusicPicker = ({ music, allSources, onPick, onUpdate, onClear }) => {
  if (music) {
    return (
      <div
        className="bg-white border border-ink-200 rounded-2xl p-4 space-y-3"
        data-testid="timeline-music-card"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Music2 className="w-5 h-5 text-violet-500" />
            <p className="text-sm font-bold text-ink-950">Music track attached</p>
          </div>
          <button
            type="button"
            onClick={onClear}
            data-testid="timeline-music-clear"
            className="text-xs font-bold text-rose-500 hover:text-rose-700"
          >
            Remove
          </button>
        </div>

        <video src={music.media_url} controls className="w-full max-h-32 rounded-xl bg-black" />

        <div className="grid grid-cols-2 gap-3 text-xs">
          <label className="flex flex-col">
            <span className="text-[9px] uppercase tracking-wider text-ink-400 font-bold mb-1">
              Volume ({Math.round((music.volume || 0) * 100)}%)
            </span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={music.volume}
              onChange={(e) => onUpdate({ volume: Number(e.target.value) })}
              data-testid="timeline-music-volume"
            />
          </label>
          <label className="flex flex-col">
            <span className="text-[9px] uppercase tracking-wider text-ink-400 font-bold mb-1">
              Start offset (s)
            </span>
            <input
              type="number"
              step="0.5"
              min="0"
              value={music.start_offset}
              onChange={(e) => onUpdate({ start_offset: Math.max(0, Number(e.target.value)) })}
              data-testid="timeline-music-offset"
              className="bg-ink-50 rounded px-2 py-1 outline-none focus:bg-white border border-ink-200"
            />
          </label>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-ink-200 rounded-2xl p-4">
      <p className="text-sm text-ink-500 mb-3">
        Pick one of your AI Video generations as a backing track. Audio gets stripped from the visuals.
      </p>
      {allSources.length === 0 ? (
        <p className="text-xs text-ink-400">
          You don&apos;t have any video generations yet — head to AI Video first.
        </p>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-8 gap-2">
          {allSources.slice(0, 16).map(s => (
            <button
              key={`m:${s.collection}:${s.id}`}
              type="button"
              onClick={() => onPick(s)}
              data-testid={`timeline-music-pick-${s.id}`}
              className="relative rounded-xl overflow-hidden border-2 aspect-square border-ink-200 hover:border-violet-500 group"
            >
              <video src={s.media_url} muted playsInline preload="metadata"
                     className="w-full h-full object-cover" />
              <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                <Music2 className="w-5 h-5 text-white opacity-0 group-hover:opacity-100" />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


export default TimelineEditorPage;
